import { useEffect, useState } from 'react';
import {
  ApiProjections,
  ListId,
  ListResponse,
  UserListResponse,
  getListByIdReqObj,
  myListApi,
  getListCategory,
  listCategoryDataTransformer,
  ListType,
  CartRoutePaths,
  getCategoryIdByCategoryName,
  MyShopStorageConstant,
  updateListsByUser,
  ErrorAPI,
  CartConstantsEnum,
} from '@commerce-cart-webcomponents/sdk';
import {
  useNextgenRouter,
  FAIcon,
  useTranslation,
  Tab,
  SelectorType,
  TabLayout,
  TabDesign,
  TextLink,
  TextLinkStyle,
  GenericLink,
  useBreakpoint,
  Breakpoints,
  useSession,
} from '@nextgen-web-framework/all';
import {
  CreateListDrawer,
  MiniCart,
  GCOList,
  useFetchCmsContent,
  useExceptionToast,
} from '@commerce-cart-webcomponents/ui';
import { StorageService } from '@nextgen-web-framework/core';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { useDispatch } from 'react-redux';
import {
  MyShop,
  myshopApi,
  ApiProjections as myShopApiProjection,
  MyShopResponse,
} from '@commerce-myshop-webcomponents/sdk';
import { EmptyList } from '../my-list-items/empty-list/EmptyList';
import styles from './MyListsPage.module.scss';
import { MyListsPageLoader } from './my-lists-page-loader/MyListsPageLoader';

export type MyListsPageProps = {
  onListUpdate?: boolean;
  isMyShop?: boolean;
  updateListLength?: (num: number) => void;
  isReceiver?: boolean;
  profileId?: string;
  accountId?: string;
  onCategoryFilterChange?: (category: string) => void;
};

export const MyListsPage = ({
  onListUpdate,
  isMyShop = false,
  isReceiver = false,
  updateListLength,
  profileId,
  accountId,
  onCategoryFilterChange,
}: MyListsPageProps) => {
  const viewPort = useBreakpoint();
  const { isABO } = useSession();
  const [showLoader, setShowLoader] = useState(true);
  const [categoryListsData, setCategoryListsData] = useState<Array<ListResponse>>([]);
  const [myListsData, setMyListsData] = useState<UserListResponse>({});
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [showListDrawer, setShowListDrawer] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const router = useNextgenRouter();
  const t = useTranslation();
  const triggerToast = useExceptionToast();
  const categoryAll = t('cart.myListsPage.all');
  const categoryData = listCategoryDataTransformer(useFetchCmsContent());

  const showMiniCart = [Breakpoints.XS, Breakpoints.SM].includes(viewPort as Breakpoints);
  const [selectedCategory, setSelectedCategory] = useState(categoryAll);
  const dispatch = useDispatch();

  function getMyShopData() {
    myshopApi
      .getMyShop({
        projections: myShopApiProjection.MIN,
      })
      .then((response: MyShopResponse) => {
        const myshopObj = response as MyShop;
        if (myshopObj.shopId) {
          StorageService.getLocalStorage().store(CartConstantsEnum.MyShopExists, myshopObj.shopId);
        } else {
          StorageService.getLocalStorage().remove(CartConstantsEnum.MyShopExists);
        }
      })
      .catch((error) => {
        throw error;
      });
  }

  useEffect(() => {
    const myShopExists = StorageService.getLocalStorage().retrieve(CartConstantsEnum.MyShopExists);
    if (isABO() && !myShopExists) {
      getMyShopData();
    }
  }, []);

  useEffect(() => {
    fetchListData();
    onCategoryFilterChange?.(selectedCategory);
  }, [onListUpdate, router.params?.locale, selectedCategory]);

  useEffect(() => {
    if (currentPage !== 0) {
      fetchListData(true);
    }
  }, [currentPage]);

  const listUpdateHandler = (listId: string, isDeleted?: boolean, isRemovedFromMyShop?: boolean) => {
    if (isDeleted || (isMyShop && isRemovedFromMyShop)) {
      const deletedListData = categoryListsData.filter((list) => list.listId !== listId);
      setCategoryListsData(deletedListData);
      setMyListsData((prevData) => ({
        listCount: (prevData?.listCount || 0) - 1,
        lists: deletedListData,
        maxCount: (prevData?.maxCount || 0) - 1,
      }));
      return;
    }

    myListApi
      .getListById({
        listId: listId as string,
      })

      .then((res: ListResponse) => {
        const updatedListData = categoryListsData.map((list) => {
          if (list.listId === listId) {
            return { ...res };
          }
          return list;
        });
        setCategoryListsData(updatedListData);
        setMyListsData((prevData) => ({
          ...prevData,
          lists: updatedListData,
        }));
      })
      .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps));
  };

  const fetchListData = (pageChanged = false) => {
    const shareCode = StorageService.getLocalStorage().retrieve(
      MyShopStorageConstant.SHARECODEATTRIBUTES,
    ) as AdvanceShareCodeResponse;
    const getListsByUserPayload = isMyShop
      ? {
          projections: ApiProjections.DETAILED,
          page: currentPage.toString(),
          displayedOnShop: true,
          category: getCategoryIdByCategoryName(selectedCategory, categoryData),
        }
      : {
          projections: ApiProjections.DETAILED,
          page: currentPage.toString(),
          category: getCategoryIdByCategoryName(selectedCategory, categoryData),
        };

    const payload = isReceiver
      ? {
          ...getListsByUserPayload,
          userId: profileId ? profileId : shareCode?.profileId,
          listType: ListType.Shareable,
          accountId: accountId,
        }
      : getListsByUserPayload;
    myListApi
      .getListsByUser(payload)
      .then((res: UserListResponse) => {
        setMyListsData((prevData) => {
          const mergedLists = pageChanged ? [...(prevData.lists || []), ...(res.lists || [])] : res.lists;
          return {
            lists: mergedLists,
            listCount: res.listCount,
            maxCount: res.maxCount,
          };
        });
        const updatedCategoryLists = pageChanged
          ? selectedCategory === categoryAll
            ? [...categoryListsData, ...(res.lists as ListResponse[])]
            : [...categoryListsData, ...getListCategory(res, selectedCategory)]
          : (res.lists as ListResponse[]);
        dispatch(
          updateListsByUser({
            lists: updatedCategoryLists,
            listCount: res.listCount,
            maxCount: res.maxCount,
          }),
        );
        setCategoryListsData(updatedCategoryLists);
        setTotalCount(res.listCount as number); //to pass list count to gcolist as prop
        updateListLength?.(res.maxCount as number);
      })
      .catch((...errorProps: ErrorAPI[]) => triggerToast(errorProps))
      .finally(() => setShowLoader(false));
  };

  const clickedShowList = (id: string) => {
    const cartReq = { ...getListByIdReqObj };
    cartReq.url = cartReq.url.replace(ListId, id);
    if (router.push) {
      router.push(cartReq.url);
    }
  };

  const onListCategoryClick = (category: string, index: number) => {
    setCurrentPage(0);
    setSelectedTab(index);
    setSelectedCategory(category);
  };

  const categories = [categoryAll, ...Array.from(categoryData.map((category) => category.category_name))];

  const onChevronLeftClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    router.back();
  };
  const noListPresentOnMyShop = isMyShop && myListsData.listCount === 0 && selectedCategory === categoryAll;
  return (
    <>
      {showListDrawer && (
        <CreateListDrawer
          isEdit={false}
          onDrawerClose={() => {
            setShowListDrawer(false);
          }}
        />
      )}

      <div className={`${styles['myList-page-container']} ${isMyShop ? styles['myList-page-container-myshop'] : ''}`}>
        <div className={styles['myList-page-container-header']}>
          {!isMyShop && (
            <>
              <span className={styles['myList-page-container-header-back']}>
                <TextLink
                  href=""
                  text={t('cart.myListsPage.back')}
                  onClick={onChevronLeftClick}
                  textLinkStyle={TextLinkStyle.CHEVRON}
                />
              </span>
              <div className={styles['myList-page-container-header-list']}>
                <span className={styles['myLists-header']}>
                  <span className={styles['left-icon']}>
                    <FAIcon iconName="chevron-left" onClick={onChevronLeftClick} />
                  </span>
                  {t('cart.myListsPage.myLists')}
                </span>
                <div className={styles['myList-page-container-header-icons']}>
                  {showMiniCart && (
                    <span className={styles['minicart-icon']}>
                      <GenericLink href={CartRoutePaths.CART}>
                        <MiniCart elementClass="myList-header-minicart" />
                      </GenericLink>
                    </span>
                  )}
                  <FAIcon className={styles['myLists-icon']} iconName="plus" onClick={() => setShowListDrawer(true)} />
                </div>
              </div>
            </>
          )}

          {(myListsData.maxCount ?? 0) > 0 && !noListPresentOnMyShop && (
            <div className={styles['myList-page-pill']}>
              {categories.map((category, index) => (
                <Tab
                  key={`pill${index + 1}`}
                  title={category as string}
                  selectorType={SelectorType.UNDERLINE}
                  layout={TabLayout.HORIZONTAL}
                  design={TabDesign.PILL}
                  onTabClick={() => onListCategoryClick(category as string, index)}
                  data-testid={`test-pill${index + 1}`}
                  selected={selectedTab === index}
                />
              ))}
            </div>
          )}
        </div>
        {showLoader ? (
          <MyListsPageLoader />
        ) : categoryListsData.length ? (
          <div className={styles['myList-page-container-lists']}>
            <GCOList
              isReceiver={isReceiver}
              myLists={categoryListsData} //this has updated listResponse  data
              clickedShowList={clickedShowList}
              isTypeList={true}
              subCarts={[]}
              listUpdatedCb={listUpdateHandler}
              categoryData={categoryData}
              totalListCount={totalCount}
              loadNextPage={() => setCurrentPage(currentPage + 1)}
              myListsData={myListsData} //this has updated UserListResponse  data
            />
          </div>
        ) : (
          (!isMyShop || !noListPresentOnMyShop) && <EmptyList isListFilter={true} />
        )}
      </div>
    </>
  );
};
